import {
  useQuery,
  useMutation,
  useQueryClient,
  type QueryClient,
  type MutateOptions,
} from "@tanstack/react-query";
import { useLayoutEffect, useMemo, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useOrganization } from "@/contexts/OrganizationContext";
import { PERSONAL_PROJECT_SCOPE } from "@/features/projects/types";
import { apiKeysRagApi, ApiKeyCreate } from "../service";
import {
  apiKeyQueryKeys,
  isApiKeyListKeyInScope,
  type ApiKeyListScope,
} from "../query-keys";
import { toast } from "sonner";
import i18n from "@/i18n";

export function extractErrorMessage(error: any, fallbackKey: string): string {
  const data = error?.response?.data;
  const message =
    data?.error?.message ||
    data?.detail ||
    data?.message;
  if (typeof message === "string" && message.trim()) return message;
  if (Array.isArray(message)) {
    const messages = message.flatMap((item) =>
      typeof item === "string" ? [item] : typeof item?.msg === "string" ? [item.msg] : [],
    ).filter((item) => item.trim());
    if (messages.length) return messages.join("\n");
  }
  return i18n.t(fallbackKey);
}

interface UseApiKeysParams {
  page: number;
  pageSize: number;
  projectIds?: string[];
}

const useApiKeyListScope = () => {
  const { currentUser } = useAuth();
  const { currentOrganization, initialized } = useOrganization();

  return {
    scope: {
      principalId: currentUser?.id ?? "",
      organizationId:
        currentOrganization?.id ?? PERSONAL_PROJECT_SCOPE,
    } satisfies ApiKeyListScope,
    initialized,
  };
};

export const invalidateApiKeyQueryScope = (
  queryClient: QueryClient,
  scope: ApiKeyListScope,
) => {
  if (!scope.principalId) return Promise.resolve();

  return queryClient.invalidateQueries({
    queryKey: apiKeyQueryKeys.listScope(scope),
  });
};

export const useApiKeys = ({ page, pageSize, projectIds }: UseApiKeysParams) => {
  const { scope, initialized } = useApiKeyListScope();

  return useQuery({
    queryKey: apiKeyQueryKeys.list(scope, { page, pageSize, projectIds }),
    queryFn: () => apiKeysRagApi.listApiKeys(page, pageSize, projectIds),
    enabled: initialized === true && Boolean(scope.principalId),
    placeholderData: (previousData, previousQuery) =>
      previousQuery &&
      isApiKeyListKeyInScope(previousQuery.queryKey, scope)
        ? previousData
        : undefined,
  });
};

type ScopedOperation<T> = {
  value: T;
  scope: ApiKeyListScope;
  owner: object;
};

// The variables own settlement: TanStack may replace a pending mutation's
// callbacks after a render, so hook-render closures cannot own its scope.
const useScopedApiKeyMutation = <TData, TResult>(
  execute: (data: TData) => Promise<TResult>,
  failureKey: string,
  successKey?: string,
) => {
  const queryClient = useQueryClient();
  const { scope } = useApiKeyListScope();
  const identity = JSON.stringify([scope.principalId, scope.organizationId]);
  const owner = useMemo(() => ({ identity }), [identity]);
  const session = useRef(owner);
  useLayoutEffect(() => { session.current = owner; }, [owner]);
  const mounted = useRef(false);
  useLayoutEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const isCurrent = (operation: ScopedOperation<TData>) =>
    mounted.current && operation.owner === session.current;

  const mutation = useMutation<TResult, unknown, ScopedOperation<TData>>({
    gcTime: 0,
    mutationFn: (operation) => execute(operation.value),
    onSuccess: (_result, operation) => {
      if (successKey && isCurrent(operation)) toast.success(i18n.t(successKey));
    },
    onError: (error, operation) => {
      if (isCurrent(operation)) toast.error(extractErrorMessage(error, failureKey));
    },
    onSettled: (_result, _error, operation) => {
      void invalidateApiKeyQueryScope(queryClient, operation.scope);
    },
  });
  const callbacks = (options?: MutateOptions<TResult, unknown, TData>): MutateOptions<TResult, unknown, ScopedOperation<TData>> => ({
    onSuccess: (result, operation, context, mutationContext) => {
      if (isCurrent(operation)) options?.onSuccess?.(result, operation.value, context, mutationContext);
    },
    onError: (error, operation, context, mutationContext) => {
      if (isCurrent(operation)) options?.onError?.(error, operation.value, context, mutationContext);
    },
    onSettled: (result, error, operation, context, mutationContext) => {
      if (isCurrent(operation)) options?.onSettled?.(result, error, operation.value, context, mutationContext);
    },
  });
  const operation = (value: TData): ScopedOperation<TData> => ({
    value, scope: { ...scope }, owner,
  });
  const ownsState = mutation.variables?.owner === owner;
  return {
    ...mutation,
    data: ownsState ? mutation.data : undefined,
    error: ownsState ? mutation.error : null,
    status: ownsState ? mutation.status : "idle" as const,
    isIdle: !ownsState || mutation.isIdle,
    isSuccess: ownsState && mutation.isSuccess,
    isError: ownsState && mutation.isError,
    isPending: ownsState && mutation.isPending,
    variables: ownsState ? mutation.variables?.value : undefined,
    mutate: (value: TData, options?: MutateOptions<TResult, unknown, TData>) =>
      mutation.mutate(operation(value), callbacks(options)),
    mutateAsync: (value: TData, options?: MutateOptions<TResult, unknown, TData>) =>
      mutation.mutateAsync(operation(value), callbacks(options)),
  };
};

export const useCreateApiKey = () => useScopedApiKeyMutation(
  (data: ApiKeyCreate) => apiKeysRagApi.createApiKey(data), "apiKeys.failedToCreate",
);
export const useDeleteApiKey = () => useScopedApiKeyMutation(
  (id: string) => apiKeysRagApi.deleteApiKey(id), "apiKeys.failedToDelete", "apiKeys.deletedSuccessfully",
);
export const useRotateApiKey = () => useScopedApiKeyMutation(
  (id: string) => apiKeysRagApi.rotateApiKey(id), "apiKeys.failedToRotate",
);
